import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  AlertCircle,
  CalendarOff,
  Check,
  Loader2,
  Search,
  Sparkles,
  X,
} from "lucide-react";
import LeagueFilterRail from "../components/p2p/create/LeagueFilterRail";
import AddFixtureCatalogRow from "../components/operator/AddFixtureCatalogRow";
import FixtureKickoffSoonBanner from "../components/operator/FixtureKickoffSoonBanner";
import OperatorConsoleShell from "../components/operator/OperatorConsoleShell";
import { selectAppUser } from "../redux/appAuthSlice";
import {
  addHouseFixture,
  fetchHouseFixtures,
  fetchOperatorDashboard,
} from "../redux/operatorConsoleSlice";
import { fetchP2PCreateFixturesByLeague, fetchP2PCreateLeagues } from "../utils/p2pCreateApi";
import { isBetHousePartnershipReadyForConsole } from "../utils/betHousePartners";
import {
  flattenLeaguesToFixtures,
  getFixtureId,
  getLeagueId,
  isUpcomingFixture,
  norm,
  rowMatchesQuery,
  searchTokensFromQuery,
} from "../utils/p2pCreateHelpers";
import { pickNextKickoffFixture, resolveFixtureId } from "../utils/fixturesDeskHelpers";
import { sortLeaguesByPriority } from "../utils/leaguePriority";
import "../betdetail.css";
import "../p2p-create.css";
import "../bettors-desk.css";
import "../add-fixtures.css";

/* ── kickoff → day grouping helpers ──────────────────────── */
const fixtureKickoffMs = (fixture) => {
  const ts = Number(fixture?.timestamp);
  if (Number.isFinite(ts) && ts > 0) return ts * 1000;
  const parsed = Date.parse(fixture?.date);
  return Number.isNaN(parsed) ? null : parsed;
};

const startOfDay = (ms) => {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

const dayMeta = (ms) => {
  const today = startOfDay(Date.now());
  const day = startOfDay(ms);
  const diff = Math.round((day - today) / 86400000);
  let label;
  if (diff === 0) label = "Today";
  else if (diff === 1) label = "Tomorrow";
  else if (diff === -1) label = "Yesterday";
  else label = new Date(ms).toLocaleDateString(undefined, { weekday: "long" });
  const dateLabel = new Date(ms).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
  return { key: day, label, dateLabel, isToday: diff === 0 };
};

const AddFixturesDesk = () => {
  const { houseId } = useParams();
  const dispatch = useDispatch();
  const userId = useSelector(selectAppUser)?._id;
  const { houses, houseFixtures } = useSelector((s) => s.operatorConsole);

  const [leaguesRaw, setLeaguesRaw] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef(null);
  const [selectedLeagueId, setSelectedLeagueId] = useState(null);
  const [addingId, setAddingId] = useState(null);
  const [optimisticAddedIds, setOptimisticAddedIds] = useState(() => new Set());
  const [toast, setToast] = useState(null);
  const [boardPulse, setBoardPulse] = useState(false);
  const prevBoardCount = useRef(0);
  const [justAddedId, setJustAddedId] = useState(null);
  // per-league extra pages loaded via "Load more"
  const [leagueExtras, setLeagueExtras] = useState({}); // { leagueId: fixture[] }
  const [leaguePaging, setLeaguePaging] = useState({}); // { leagueId: { page, hasMore, loading } }

  const bet = useMemo(
    () => houses.find((h) => String(h._id) === String(houseId)),
    [houses, houseId]
  );
  const partnershipReady = isBetHousePartnershipReadyForConsole(bet);
  const houseName = bet?.name || "House";

  useEffect(() => {
    if (!toast) return undefined;
    const id = window.setTimeout(() => setToast(null), 4200);
    return () => window.clearTimeout(id);
  }, [toast]);

  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);

  useEffect(() => {
    if (!houseId) return undefined;
    dispatch(fetchHouseFixtures({ betId: houseId }));
    return undefined;
  }, [dispatch, houseId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchP2PCreateLeagues(10);
        if (!cancelled) setLeaguesRaw(data);
      } catch (e) {
        if (!cancelled) setError(e.message || "Could not load leagues");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const addedIds = useMemo(
    () => new Set(houseFixtures.map((f) => String(resolveFixtureId(f)))),
    [houseFixtures]
  );

  const effectiveAddedIds = useMemo(() => {
    const merged = new Set(addedIds);
    optimisticAddedIds.forEach((id) => merged.add(id));
    return merged;
  }, [addedIds, optimisticAddedIds]);

  const boardCount = useMemo(() => {
    let count = houseFixtures.filter(isUpcomingFixture).length;
    optimisticAddedIds.forEach((id) => {
      if (!addedIds.has(id)) count += 1;
    });
    return count;
  }, [houseFixtures, addedIds, optimisticAddedIds]);

  const nextKickoffFixture = useMemo(
    () => pickNextKickoffFixture(houseFixtures),
    [houseFixtures]
  );

  // Pulse the board indicator whenever the count climbs.
  useEffect(() => {
    if (boardCount > prevBoardCount.current) {
      setBoardPulse(true);
      const id = window.setTimeout(() => setBoardPulse(false), 620);
      prevBoardCount.current = boardCount;
      return () => window.clearTimeout(id);
    }
    prevBoardCount.current = boardCount;
    return undefined;
  }, [boardCount]);

  useEffect(() => {
    if (!optimisticAddedIds.size) return undefined;
    setOptimisticAddedIds((prev) => {
      const next = new Set(prev);
      let changed = false;
      prev.forEach((id) => {
        if (addedIds.has(id)) {
          next.delete(id);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [addedIds, optimisticAddedIds.size]);

  const allRows = useMemo(
    () => {
      const base = flattenLeaguesToFixtures(leaguesRaw, Date.now(), { upcomingOnly: true });
      // Merge any extra pages for individual leagues
      const extraEntries = Object.entries(leagueExtras);
      if (!extraEntries.length) return base;
      const existingIds = new Set(base.map((r) => String(getFixtureId(r.fixture))));
      const extra = extraEntries.flatMap(([lid, fixtures]) => {
        const league = leaguesRaw.find((l) => String(getLeagueId(l)) === String(lid));
        const leagueMeta = league
          ? {
              id: getLeagueId(league),
              name: league.leagueName || "League",
              country: league.countryName || "",
              logo: league.leagueLogo || null,
            }
          : { id: lid };
        return fixtures
          .filter(isUpcomingFixture)
          .filter((f) => !existingIds.has(String(getFixtureId(f))))
          .map((fixture) => ({ fixture, league: leagueMeta }));
      });
      return [...base, ...extra];
    },
    [leaguesRaw, leagueExtras]
  );

  /** Per-league upcoming counts — derived from the same rows shown in the catalog. */
  const leagueUpcomingCounts = useMemo(() => {
    const counts = new Map();
    for (const row of allRows) {
      const lid = String(row.league?.id ?? "");
      if (!lid) continue;
      counts.set(lid, (counts.get(lid) || 0) + 1);
    }
    return counts;
  }, [allRows]);

  const leagueOptions = useMemo(
    () =>
      sortLeaguesByPriority(
        leaguesRaw.map((league) => {
          const leagueId = getLeagueId(league);
          return {
            id: leagueId,
            apiId: league?.id ?? null,
            name: league?.leagueName || "League",
            country: league?.countryName || "",
            logo: league?.leagueLogo || null,
            count: leagueUpcomingCounts.get(String(leagueId)) ?? 0,
          };
        })
      ),
    [leaguesRaw, leagueUpcomingCounts]
  );

  const tokens = useMemo(() => searchTokensFromQuery(search), [search]);

  const visibleRows = useMemo(() => {
    let rows = allRows;
    if (selectedLeagueId) {
      rows = rows.filter((r) => String(r.league?.id) === String(selectedLeagueId));
    }
    if (tokens.length) {
      rows = rows.filter((r) => rowMatchesQuery(r, tokens));
    }
    return rows;
  }, [allRows, selectedLeagueId, tokens]);

  /** Group the visible catalog by kickoff day so operators scan a matchday at a time. */
  const groupedRows = useMemo(() => {
    const map = new Map();
    for (const row of visibleRows) {
      const ms = fixtureKickoffMs(row.fixture);
      const meta =
        ms == null
          ? { key: Number.MAX_SAFE_INTEGER, label: "Date TBD", dateLabel: "", isToday: false }
          : dayMeta(ms);
      if (!map.has(meta.key)) map.set(meta.key, { ...meta, rows: [] });
      map.get(meta.key).rows.push(row);
    }
    return [...map.values()].sort((a, b) => a.key - b.key);
  }, [visibleRows]);

  const isSearching = norm(search).length > 0;
  const totalUpcoming = allRows.length;
  const totalAvailable = useMemo(
    () =>
      allRows.filter((row) => !effectiveAddedIds.has(String(getFixtureId(row.fixture)))).length,
    [allRows, effectiveAddedIds]
  );

  const handleSelectLeague = (id) => {
    setSelectedLeagueId(id);
  };

  const currentPaging = selectedLeagueId ? leaguePaging[selectedLeagueId] : null;
  const canLoadMore =
    Boolean(selectedLeagueId) &&
    !isSearching &&
    (currentPaging == null || currentPaging.hasMore !== false) &&
    !loading;

  const handleLoadMore = async () => {
    if (!selectedLeagueId || !canLoadMore) return;
    const nextPage = (currentPaging?.page ?? 1) + 1;
    setLeaguePaging((prev) => ({
      ...prev,
      [selectedLeagueId]: { ...prev[selectedLeagueId], loading: true },
    }));
    try {
      const { fixtures, pagination } = await fetchP2PCreateFixturesByLeague(
        selectedLeagueId,
        nextPage,
        25
      );
      setLeagueExtras((prev) => ({
        ...prev,
        [selectedLeagueId]: [...(prev[selectedLeagueId] || []), ...(fixtures || [])],
      }));
      setLeaguePaging((prev) => ({
        ...prev,
        [selectedLeagueId]: {
          page: pagination?.page ?? nextPage,
          hasMore: pagination?.hasNextPage ?? false,
          loading: false,
        },
      }));
    } catch {
      setLeaguePaging((prev) => ({
        ...prev,
        [selectedLeagueId]: { ...prev[selectedLeagueId], loading: false },
      }));
    }
  };

  const handleAddFixture = async ({ fixture }) => {
    const fixtureId = String(getFixtureId(fixture));
    if (!partnershipReady || !userId || !houseId || addingId) return;

    setAddingId(fixtureId);
    try {
      await dispatch(
        addHouseFixture({ betId: houseId, fixtureId, operatorUserId: userId })
      ).unwrap();

      const home = fixture?.homeTeam?.name || "Home";
      const away = fixture?.awayTeam?.name || "Away";
      setOptimisticAddedIds((prev) => new Set(prev).add(fixtureId));
      setJustAddedId(fixtureId);
      window.setTimeout(() => {
        setJustAddedId((cur) => (cur === fixtureId ? null : cur));
      }, 600);
      setToast({
        type: "success",
        title: "Added to board",
        detail: `${home} vs ${away}`,
      });

      dispatch(fetchHouseFixtures({ betId: houseId }));
      dispatch(fetchOperatorDashboard(houseId));
    } catch (err) {
      setToast({
        type: "error",
        title: "Could not add fixture",
        detail: typeof err === "string" ? err : "Try again in a moment.",
      });
    } finally {
      setAddingId(null);
    }
  };

  if (!houseId) {
    return (
      <OperatorConsoleShell activeSection="fixtures" mainClassName="oc-desk-main">
        <div className="bd-page fd-page afx-page">
          <div className="afx-state">
            <h3>Missing house</h3>
            <p>Select a betting house from the console to add fixtures.</p>
          </div>
        </div>
      </OperatorConsoleShell>
    );
  }

  return (
    <OperatorConsoleShell activeSection="fixtures" mainClassName="oc-desk-main">
      <div className="bd-page fd-page afx-page">
      <header className="fd-operator-header afx-header" aria-label="Board builder">
        <div className="fd-desk-bar">
          <div className="fd-desk-bar-start fd-desk-header-copy">
            <p className="bd-eyebrow">
              <Sparkles size={11} aria-hidden />
              Board builder
            </p>
            <span title={houseName}>
              {houseName} · Stock the operator board
            </span>
          </div>

          <div className="fd-desk-bar-center">
            <FixtureKickoffSoonBanner fixture={nextKickoffFixture} />
          </div>

          <div className="fd-desk-bar-end">
            <div className="afx-search-slot">
              {searchOpen ? (
                <div className="fd-desk-search-popover">
                  <div className="fd-desk-search">
                    <Search size={14} aria-hidden />
                    <input
                      ref={searchInputRef}
                      type="search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search teams…"
                      aria-label="Search teams"
                    />
                    {search ? (
                      <button
                        type="button"
                        className="fd-desk-search-clear"
                        aria-label="Clear search"
                        onClick={() => setSearch("")}
                      >
                        <X size={14} />
                      </button>
                    ) : null}
                  </div>
                </div>
              ) : null}
              <button
                type="button"
                className={[
                  "bd-chrome-btn",
                  "afx-search-toggle",
                  searchOpen ? "afx-search-toggle-on" : "",
                  !searchOpen && search ? "afx-search-toggle-active" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => setSearchOpen((open) => !open)}
                aria-label={searchOpen ? "Close search" : "Search teams"}
                aria-expanded={searchOpen}
              >
                {searchOpen ? (
                  <X size={15} strokeWidth={2.3} />
                ) : (
                  <Search size={15} strokeWidth={2.3} />
                )}
              </button>
            </div>

            <span
              className={`afx-board tabular-nums${boardPulse ? " afx-board-pulse" : ""}`}
              aria-label={`${boardCount} fixtures on this board`}
            >
              {boardCount}
            </span>
          </div>
        </div>
      </header>

      {!partnershipReady ? (
        <div className="afx-alert" role="status">
          <AlertCircle size={15} aria-hidden />
          <p style={{ margin: 0 }}>
            All partners must accept before you can add fixtures to this house.
          </p>
        </div>
      ) : null}

      {toast ? (
        <div
          className={`afx-toast afx-toast-${toast.type === "error" ? "error" : "success"}`}
          role="status"
          aria-live="polite"
        >
          {toast.type === "error" ? (
            <AlertCircle size={16} aria-hidden />
          ) : (
            <Check size={16} aria-hidden />
          )}
          <span className="afx-toast-text">
            <strong>{toast.title}</strong>
            {toast.detail ? <em>{toast.detail}</em> : null}
          </span>
        </div>
      ) : null}

      <div className="bd-desk-body afx-desk-body">
      <div className="afx-split">
        {!error ? (
          <LeagueFilterRail
            leagues={loading ? [] : leagueOptions}
            selectedId={selectedLeagueId}
            onSelect={handleSelectLeague}
            totalMatches={loading ? 0 : totalUpcoming}
            loading={loading}
          />
        ) : null}

        <main className="afx-catalog">
          <div className="afx-scroll">
            {loading ? (
              <div className="afx-state">
                <span className="afx-state-icon">
                  <Loader2 size={24} className="afx-spin" />
                </span>
                <h3>Loading the catalog</h3>
                <p>Pulling in leagues &amp; upcoming matches…</p>
              </div>
            ) : null}

            {!loading && error ? (
              <div className="afx-state afx-state-err">
                <span className="afx-state-icon">
                  <AlertCircle size={24} />
                </span>
                <h3>Something went wrong</h3>
                <p>{error}</p>
              </div>
            ) : null}

            {!loading && !error && visibleRows.length === 0 ? (
              <div className="afx-state">
                <span className="afx-state-icon">
                  <CalendarOff size={24} />
                </span>
                <h3>
                  {isSearching
                    ? "No teams match your search"
                    : totalAvailable === 0 && allRows.length > 0
                    ? "Your board is fully stocked"
                    : "No upcoming matches"}
                </h3>
                <p>
                  {isSearching
                    ? "Try another team name or clear the search."
                    : totalAvailable === 0 && allRows.length > 0
                    ? "Every catalog fixture is already on the board. Head back to the fixtures desk to manage them."
                    : selectedLeagueId
                    ? "This league has no fixtures right now. Try another league."
                    : "Check back soon — new fixtures are added regularly."}
                </p>
              </div>
            ) : null}

            {!loading && !error && visibleRows.length > 0 ? (
              <>
                {groupedRows.map((group) => (
                  <section className="afx-day" key={group.key}>
                    <div className="afx-day-head">
                      <span
                        className={`afx-day-badge${group.isToday ? " afx-day-today" : ""}`}
                      >
                        <strong>{group.label}</strong>
                        {group.dateLabel ? <span>{group.dateLabel}</span> : null}
                      </span>
                      <span className="afx-day-line" aria-hidden />
                      <span className="afx-day-count">
                        {group.rows.length} {group.rows.length === 1 ? "match" : "matches"}
                      </span>
                    </div>

                    <div className="afx-runway">
                      {group.rows.map((row) => {
                        const fixtureId = String(getFixtureId(row.fixture));
                        const isAdded = effectiveAddedIds.has(fixtureId);
                        return (
                          <AddFixtureCatalogRow
                            key={fixtureId}
                            row={row}
                            hideLeague={Boolean(selectedLeagueId)}
                            onAdd={handleAddFixture}
                            busy={addingId === fixtureId}
                            added={isAdded}
                            stamped={justAddedId === fixtureId}
                            disabled={
                              !partnershipReady ||
                              isAdded ||
                              Boolean(addingId && addingId !== fixtureId)
                            }
                          />
                        );
                      })}
                    </div>
                  </section>
                ))}

                {canLoadMore ? (
                  <button
                    type="button"
                    className="afx-load-more"
                    onClick={handleLoadMore}
                    disabled={currentPaging?.loading}
                  >
                    {currentPaging?.loading ? (
                      <>
                        <Loader2 size={15} className="afx-spin" /> Loading…
                      </>
                    ) : (
                      "Load more fixtures"
                    )}
                  </button>
                ) : null}
              </>
            ) : null}
          </div>
        </main>
      </div>
      </div>
      </div>
    </OperatorConsoleShell>
  );
};

export default AddFixturesDesk;
